import { ActionCreator, Dispatch } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { apiClient } from '../../services/apiService';
import {
  isConflictResponse,
  isValidResponse,
} from '../../services/responseService';
import {
  DistributorApiDTO,
  IMXUserInformation,
  UserDistributorDTO,
} from '../roles/types';
import { UsersActions, UsersActionTypes } from './actionTypes';
import { IUsersState } from './store';
import {
  ExtendedUser,
  ResponseMessage,
  SingleUsersRolesInformation,
  UserAccess,
  UserRolesInformationDTO,
  UsersRolesInformation,
  UserIdentityInfo,
} from './types';

export const getErrorStatusFromError = (err: any) =>
  parseInt(err.message.split(' ').reverse()[0] || '0', 10);

export const fetchUsers: ActionCreator<ThunkAction<
  Promise<any>,
  IUsersState,
  null,
  UsersActions
>> = () => {
  return async (dispatch: Dispatch<UsersActions>) => {
    console.log('>> redux > fetchUsers');
    try {
      const { data } = await apiClient.get<ExtendedUser[]>(
        `/api/usersManagement/users`
      );
      dispatch({
        type: UsersActionTypes.FETCH_USERS_SUCCESS,
        users: data,
      });
    } catch (err) {
      dispatch({
        type: UsersActionTypes.FETCH_USERS_ERROR,
        status: 500,
      });
    }
  };
};

export const postUsers: ActionCreator<ThunkAction<
  Promise<any>,
  IUsersState,
  null,
  UsersActions
>> = (users: ExtendedUser[]) => {
  return async (dispatch: Dispatch<UsersActions>) => {
    console.log('>> redux > postUsers');

    const dispatchError = (status: number, message: string) =>
      dispatch({
        type: UsersActionTypes.POST_USERS_ERROR,
        status,
        message,
      });
    try {
      const data = await apiClient.post(
        `/api/usersManagement/users`,
        JSON.stringify(users),
        {
          headers: {
            accept: 'text/plain',
          },
          validateStatus: function (status) {
            return status === 409 || isValidResponse(status);
          },
        }
      );
      if (
        !isValidResponse(data.status || 200) &&
        !isConflictResponse(data.status)
      )
        return dispatchError(data.status, data.statusText);
      dispatch({
        type: UsersActionTypes.POST_USERS_SUCCESS,
        users,
        status: data.status,
      });
    } catch (err) {
      dispatchError(getErrorStatusFromError(err), err?.toString() ?? 'Error');
    }
    return;
  };
};

// export const postUsersRoles: ActionCreator<ThunkAction<
//   Promise<any>,
//   IUsersState,
//   null,
//   UsersActions
// >> = (userRolesInformation: UsersRolesInformation) => {
//   return async (dispatch: Dispatch<UsersActions>) => {
//     const dispatchError = (status: number, message: string) =>
//       dispatch({
//         type: UsersActionTypes.POST_USERS_ROLES_ERROR,
//         status,
//         message,
//       });

//     try {
//       const { data } = await apiClient.post<ResponseMessage>(
//         `/api/usersManagement/users/roles`,
//         JSON.stringify(userRolesInformation)
//       );
//       if (!isValidResponse(data?.Status || 200))
//         return dispatchError(data.Status, data.Title);
//       dispatch({
//         type: UsersActionTypes.POST_USERS_ROLES_SUCCESS,
//         userRolesInformation,
//       });
//     } catch (err) {
//       console.error('Error: ' + err);
//       dispatchError(getErrorStatusFromError(err), err?.toString() ?? 'Error');
//     }
//     return;
//   };
// };

export const postUsersRoles: ActionCreator<ThunkAction<
  Promise<any>,
  IUsersState,
  null,
  UsersActions
>> = (singleUsersRolesInformation: SingleUsersRolesInformation) => {
  return async (dispatch: Dispatch<UsersActions>) => {
    const dispatchError = (status: number, message: string) =>
      dispatch({
        type: UsersActionTypes.POST_SINGLE_USER_ROLE_ERROR,
        status,
        message,
      });

    try {
      const singleUserRole = {
        user: singleUsersRolesInformation.user,
        usersRoles: singleUsersRolesInformation.usersRoles,
      };

      const { data } = await apiClient.post<ResponseMessage>(
        `/api/usersManagement/users/roles/single`,
        JSON.stringify(singleUserRole)
      );

      if (!isValidResponse(data?.Status || 200)) {
        dispatchError(data.Status, data.Title);
        return;
      }

      dispatch({
        type: UsersActionTypes.POST_SINGLE_USER_ROLE_SUCCESS,
        singleUsersRolesInformation: singleUserRole,
      });

      return data;
    } catch (err) {
      console.error('Unexpected error:', err);
      dispatchError(
        getErrorStatusFromError(err),
        err?.toString() ?? 'Error inesperado'
      );
      return Promise.reject(err);
    }
  };
};

export const postMapApplication: ActionCreator<ThunkAction<
  Promise<any>,
  IUsersState,
  null,
  UsersActions
>> = (userRolesInformation: UsersRolesInformation) => {
  return async (dispatch: Dispatch<UsersActions>) => {
    const dispatchError = (status: number, message: string) =>
      dispatch({
        type: UsersActionTypes.POST_MAP_APPLICATIONS_ERROR,
        message,
      });

    try {
      const { data } = await apiClient.post<ResponseMessage>(
        `/api/usersManagement/users/applications`,
        JSON.stringify(userRolesInformation)
      );
      if (!isValidResponse(data?.Status || 200))
        return dispatchError(data.Status, data.Title);
      dispatch({
        type: UsersActionTypes.POST_MAP_APPLICATIONS_SUCCESS,
        status: 200,
      });
    } catch (err) {
      console.error('Error: ' + err);
      dispatchError(getErrorStatusFromError(err), err?.toString() ?? 'Error');
    }
    return;
  };
};

export const postIMXDataMapApplication: ActionCreator<ThunkAction<
  Promise<any>,
  IUsersState,
  null,
  UsersActions
>> = (imxUserInformationDTO: IMXUserInformation) => {
  return async (dispatch: Dispatch<UsersActions>) => {
    const dispatchError = (status: number, message: string) =>
      dispatch({
        type: UsersActionTypes.POST_IMX_DATA_ERROR,
        status,
        message,
      });

    try {
      const { data } = await apiClient.post<ResponseMessage>(
        `/api/usersManagement/users/imx`,
        JSON.stringify(imxUserInformationDTO)
      );
      if (!isValidResponse(data?.Status || 200))
        return dispatchError(data.Status, data.Title);
      dispatch({
        type: UsersActionTypes.POST_IMX_DATA_SUCCESS,
        status: 200,
      });
    } catch (err) {
      console.error('Error: ' + err);
      dispatchError(getErrorStatusFromError(err), err?.toString() ?? 'Error');
    }
    return;
  };
};

export const putUser: ActionCreator<ThunkAction<
  Promise<any>,
  IUsersState,
  null,
  UsersActions
>> = (user: ExtendedUser) => {
  return async (dispatch: Dispatch<UsersActions>) => {
    const dispatchError = (status: number, message: string) =>
      dispatch({
        type: UsersActionTypes.PUT_USER_ERROR,
        status,
        message,
      });

    try {
      const { data } = await apiClient.put<ResponseMessage>(
        `/api/usersManagement/users/${user.userName}`,
        JSON.stringify({
          globalId: user.globalId,
        }),
        {
          headers: {
            accept: 'text/plain',
          },
        }
      );
      if (!isValidResponse(data?.Status || 200))
        return dispatchError(data.Status, data.Title);
      dispatch({
        type: UsersActionTypes.PUT_USER_SUCCESS,
        user,
      });
    } catch (err) {
      console.log(err);
      dispatchError(getErrorStatusFromError(err), err?.toString() ?? 'Error');
    }
    return;
  };
};

export const patchUser: ActionCreator<ThunkAction<
  Promise<any>,
  IUsersState,
  null,
  UsersActions
>> = (user: ExtendedUser) => {
  return async (dispatch: Dispatch<UsersActions>) => {
    const dispatchError = (status: number, message: string) =>
      dispatch({
        type: UsersActionTypes.PATCH_USER_ERROR,
        status,
        message,
      });

    try {
      const { data } = await apiClient.patch<ResponseMessage>(
        `/api/usersManagement/users`,
        JSON.stringify(user),
        {
          headers: {
            accept: 'text/plain',
          },
        }
      );
      if (!isValidResponse(data?.Status || 200))
        return dispatchError(data.Status, data.Title);
      dispatch({
        type: UsersActionTypes.PATCH_USER_SUCCESS,
        user,
      });
    } catch (err) {
      console.log(err);
      dispatchError(getErrorStatusFromError(err), err?.toString() ?? 'Error');
    }
    return;
  };
};

export const deleteUser: ActionCreator<ThunkAction<
  Promise<any>,
  IUsersState,
  null,
  UsersActions
>> = (userName: string, userEmail: string) => {
  return async (dispatch: Dispatch<UsersActions>) => {
    const dispatchError = (status: number, message: string) =>
      dispatch({
        type: UsersActionTypes.DELETE_USER_ERROR,
        status,
        message,
      });

    try {
      const { data } = await apiClient.delete<ResponseMessage>(
        `/api/usersManagement/users/${userName}/${userEmail}`
      );
      if (!isValidResponse(data?.Status || 200))
        return dispatchError(data.Status, data.Title);
      dispatch({
        type: UsersActionTypes.DELETE_USER_SUCCESS,
        userName,
      });
    } catch (err) {
      console.log(err);
      dispatchError(getErrorStatusFromError(err), err?.toString() ?? 'Error');
    }
    return;
  };
};

export const clearUsersError: ActionCreator<ThunkAction<
  Promise<any>,
  IUsersState,
  null,
  UsersActions
>> = () => {
  return async (dispatch: Dispatch<UsersActions>) => {
    dispatch({ type: UsersActionTypes.CLEAR_USERS_ERROR });
  };
};

export const fetchUsersAndWaitingForStatus: ActionCreator<ThunkAction<
  Promise<any>,
  IUsersState,
  null,
  UsersActions
>> = (waitingForStatusUsers: ExtendedUser[]) => {
  return async (dispatch: Dispatch<UsersActions>) => {
    if (waitingForStatusUsers.length === 0) return;
    try {
      const { data } = await apiClient.get<ExtendedUser[]>(
        `/api/usersManagement/users`
      );
      const users = data.filter(
        (_user) =>
          waitingForStatusUsers.some(
            (user) =>
              _user.userName.toLowerCase() === user.userName.toLowerCase()
          ) &&
          !waitingForStatusUsers.some(
            (user) => JSON.stringify(_user) === JSON.stringify(user)
          )
      );
      if (users.length === 0) return;

      dispatch({
        type: UsersActionTypes.POST_USERS_SUCCESS,
        users,
        status: 200,
      });
    } catch (err) {
      dispatch({
        type: UsersActionTypes.FETCH_USERS_ERROR,
        status: 500,
      });
    }
  };
};

export const clearWaitingUsers: ActionCreator<ThunkAction<
  Promise<any>,
  IUsersState,
  null,
  UsersActions
>> = (userNames: string[]) => {
  return async (dispatch: Dispatch<UsersActions>) => {
    dispatch({
      type: UsersActionTypes.CLEAR_WAITING_FOR_USERS,
      userNames,
    });
  };
};

export const getUsersRolesInformation: ActionCreator<ThunkAction<
  Promise<any>,
  IUsersState,
  null,
  UsersActions
>> = (userName: string, userEmail: string) => {
  return async (dispatch: Dispatch<UsersActions>) => {
    try {
      dispatch({ type: UsersActionTypes.CLEAR_USER_ROLE_INFORMATION });

      const { data } = await apiClient.get<UserRolesInformationDTO>(
        `/api/usersManagement/roles/inforoleuser/${userName}`
      );
      const { data: distributors } = await apiClient.get<DistributorApiDTO[]>(
        `/api/usersManagement/distributors/${userEmail}`
      );
      const user = { ...data, distributors: distributors };

      dispatch({
        type: UsersActionTypes.GET_USER_ROLE_INFORMATION,
        user: user,
      });
    } catch (err) {
      dispatch({
        type: UsersActionTypes.GET_USER_ROLE_INFORMATION_ERROR,
        status: 500,
      });
    }
  };
};

export const postResetPassword: ActionCreator<ThunkAction<
  Promise<any>,
  IUsersState,
  null,
  UsersActions
>> = (
  user: ExtendedUser,
  okCallback: () => void,
  failedCallback: () => void
) => {
  return async (dispatch: Dispatch<UsersActions>) => {
    const dispatchError = (status: number, message: string) =>
      dispatch({
        type: UsersActionTypes.POST_RESET_PASSWORD_ERROR,
        status,
        message,
      });

    try {
      const { data } = await apiClient.post<ResponseMessage>(
        `/api/usersManagement/users/reset-password`,
        JSON.stringify(user.userName)
      );
      okCallback();
      if (!isValidResponse(data?.Status || 200))
        return dispatchError(data.Status, data.Title);
    } catch (err) {
      failedCallback();
      console.error('Error: ' + err);
      dispatchError(getErrorStatusFromError(err), err?.toString() ?? 'Error');
    }
    return;
  };
};

export const fetchPortals: ActionCreator<ThunkAction<
  Promise<any>,
  UserAccess,
  null,
  UsersActions
>> = () => {
  return async (dispatch: Dispatch<UsersActions>) => {
    try {
      dispatch({
        type: UsersActionTypes.FETCH_PORTALS_REQUEST,
        isFetching: true,
        status: 200,
      });
      const response = await apiClient.get<UserAccess>(
        `/api/usersManagement/userSettings/settings/userAccess`
      );
      dispatch({
        type: UsersActionTypes.FETCH_PORTALS_SUCCESS,
        isFetching: false,
        status: 200,
        userAccess: response.data,
      });
    } catch (err) {
      dispatch({
        type: UsersActionTypes.FETCH_PORTALS_ERROR,
        isFetching: false,
        status: 500,
      });
    }
  };
};

export const postDistributorsAccount: ActionCreator<ThunkAction<
  Promise<any>,
  IUsersState,
  null,
  UsersActions
>> = (distributorInfo: UserDistributorDTO) => {
  return async (dispatch: Dispatch<UsersActions>) => {
    const dispatchError = (status: number, message: string) =>
      dispatch({
        type: UsersActionTypes.POST_DISTRIBUTORS_ERROR,
        message: message,
      });

    try {
      const { data } = await apiClient.post<ResponseMessage>(
        `/api/usersManagement/distributors`,
        distributorInfo
      );
      if (!isValidResponse(data?.Status || 200))
        return dispatchError(data.Status, data.Title);
      dispatch({
        type: UsersActionTypes.POST_DISTRIBUTORS_SUCCESS,
        status: 200,
      });
    } catch (err) {
      console.error('Error: ' + err);
      dispatchError(getErrorStatusFromError(err), err?.toString() ?? 'Error');
    }
    return;
  };
};

export const getUserInIdentityManager: ActionCreator<ThunkAction<
  Promise<any>,
  IUsersState,
  null,
  UsersActions
>> = (userEmail: string) => {
  return async (dispatch: Dispatch<UsersActions>) => {
    console.log('>> redux > getUserInIdentityManager');
    try {
      const { data, status } = await apiClient.get<UserIdentityInfo>(
        `/api/usersmanagement/users/user-in-identity-manager/${userEmail}`
      );
      dispatch({
        type: UsersActionTypes.GET_USER_IDENTITY_SUCCESS,
        userIdentityInfo: { user: data, status: status },
      });
    } catch (err) {
      dispatch({
        type: UsersActionTypes.GET_USER_IDENTITY_ERROR,
        userIdentityInfo: {
          user: undefined,
          status: getErrorStatusFromError(err),
        },
        status: getErrorStatusFromError(err),
      });
    }
  };
};
